/**
 * The currency sentinel, and the two rules every reader of it has to agree on.
 *
 * 'nill' carries two meanings, and keeping them straight is the whole point of this
 * file:
 *
 *   - STORED in statistics.currency_code it is literal: this event carried no
 *     currency. The hourly aggregation writes it for any event whose payload had
 *     none (0001_aggregate_hourly_stats.sql).
 *   - REQUESTED as a currency selection it is an aggregate: every currency at once.
 *     Only for counts -- summing minor units across currencies would add SEK onto
 *     EUR, so for revenue a request for 'nill' keeps the literal sense.
 *
 * The string was previously written out at fifteen call sites across the crons, the
 * statistics API, the attention queue and four dashboard screens, and the "prefer the
 * aggregate bucket" rule was reimplemented independently in useExperimentStats and in
 * implementation-verdict. They did not disagree by luck rather than by design: the
 * stored procedure never emitted an aggregate bucket for a multi-currency experiment,
 * so every one of those readers silently fell through to whichever currency sorted
 * first.
 */

/**
 * These two MUST hold the same value. They are separate names so each call site can say
 * which sense it means, not so the values can diverge: the stored procedures compare the
 * requested currency against the stored column directly
 * (`p_currency = 'nill' AND p_goal <> 'Revenue'` in 0004), so changing one and not the
 * other silently stops the aggregate bucket matching the rows it is meant to aggregate.
 * If you are here to "fix" the duplication, the fix is in the SQL, not in this file.
 */

/** Requested: every currency at once. Only meaningful for counts, never for revenue. */
export const ALL_CURRENCIES = 'nill';

/** Stored: this event arrived with no currency of its own. Same string, literal sense. */
export const NO_CURRENCY = 'nill';

/**
 * Which currency bucket to read when the caller has not chosen one.
 *
 * The aggregate when it exists, otherwise the only sensible alternative -- the first
 * bucket present. Every surface that reports an experiment without a currency selector
 * resolves it here, so the statistics screen, the implementation verdict and the
 * attention queue cannot describe one experiment three ways.
 */
export const resolveReportCurrency = (currencies: readonly string[]): string | null => {
  if (currencies.length === 0) return null;
  return currencies.includes(ALL_CURRENCIES) ? ALL_CURRENCIES : (currencies[0] ?? null);
};

/** True when the aggregate bucket is this site's only currency rather than a total of several. */
export const isSingleCurrencySite = (currencies: readonly string[]): boolean =>
  currencies.length === 1 && currencies[0] === ALL_CURRENCIES;

/**
 * Revenue needs one currency; conversions do not.
 *
 * Under "All currencies" a revenue figure would cover only the traffic recorded
 * without a currency: a subset presented as a total.
 */
export const revenueNeedsCurrency = (goal: string, currency: string | null, currencies: readonly string[]): boolean =>
  goal === 'Revenue' && currency === ALL_CURRENCIES && currencies.length > 1;
